import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, Input, OnDestroy, Optional, Self, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormControl, FormGroup, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';

export class NumberRangeControl {
  constructor(public from: string, public to: string) { }
}

@Component({
  selector: 'number-range-input',
  templateUrl: './number-range-input.component.html',
  styleUrls: ['./number-range-input.component.scss'],
  providers: [{ provide: MatFormFieldControl, useExisting: NumberRangeComponent }],
  host: {
    '[class.floating]': 'shouldLabelFloat',
    '[class.show-checkbox]': 'showCheckbox',
    '[id]': 'id',
  }
})
export class NumberRangeComponent implements MatFormFieldControl<NumberRangeControl>, ControlValueAccessor, OnDestroy {
  @ViewChild('from', { read: ElementRef }) fromInput!: ElementRef<HTMLInputElement>;
  @ViewChild('to', { read: ElementRef }) toInput!: ElementRef<HTMLInputElement>;

  static nextId = 0;

  parts: FormGroup = this._formBuilder.group({
    from: new FormControl(''),
    to: new FormControl(''),
  });

  stateChanges = new Subject<void>();
  focused = false;
  touched = false;
  isRange = false;
  controlType = 'number-range-input';
  id = `number-range-input-${NumberRangeComponent.nextId++}`;
  onChange = (_: any) => { };
  onTouched = () => { };

  @Input() rangeType: 'number' | 'amount' = 'number';
  @Input() name: '';

  get empty() {
    let n = this.parts.value;
    return !n.from && !n.to;
  }

  get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  get showCheckbox() {
    return this.focused || !this.empty;
  }

  @Input()
  get value(): NumberRangeControl | null {
    let n = this.parts.value;
    return new NumberRangeControl(n.from, n.to);
  }
  set value(range: NumberRangeControl | null) {
    range = range || new NumberRangeControl('', '');
    this.parts.setValue({ from: range.from, to: range.to });
    this.stateChanges.next();
  }

  get errorState(): boolean {
    return (this.parts.invalid || parseFloat(this.parts.value.from) > parseFloat(this.parts.value.to)) && (this.focused || this.touched);
  }

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.parts.disable() : this.parts.enable();
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder: string;

  constructor(private _elementRef: ElementRef<HTMLElement>,
    private _formBuilder: FormBuilder,
    private _focusMonitor: FocusMonitor,
    @Optional() @Self() public ngControl: NgControl,) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  onShowRange() {
    this.isRange = !this.isRange;    
    //var checked = this.isRange;
    if (this.isRange) {
      this.parts.patchValue({ to: '' });
    }
    else {
      this.parts.patchValue({ to: this.parts.get('from')?.value });
    }
    this.onChange(this.value);

    setTimeout(() => {
      if (this.isRange && this.value.from) {
        this.toInput?.nativeElement?.focus();
      } else {
        this.fromInput?.nativeElement?.focus();
      }
    });
    
    this.stateChanges.next();
  }

  onFocusIn(event: FocusEvent) {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent) {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      if (!this.value?.from && !this.value?.to) {
        this.isRange = false;
      }
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement.querySelector(
      '.number-range-input-container',
    )!;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick() { }

  writeValue(range: NumberRangeControl | null): void {
    this.value = range;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onValueChange(): void {
    if (!this.isRange) {
      this.parts.patchValue({
        to: this.parts.get('from')?.value
      });
    }
    this.onChange(this.value);
  }

  previousValueTo = '';
  autoFocusFrom(control: AbstractControl): void {
    const currentValue = control.value;
    if (this.previousValueTo === '' && currentValue.length === 0) {
      this._focusMonitor.focusVia(this.fromInput, 'program');
    }
    this.previousValueTo = this.value.to;
  }
}